import React, { memo } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ArticleSection } from './components/ArticleSection';
import { Callout } from './components/Callout';
import { PullQuote } from './components/PullQuote';
import { StageBreakdown } from './components/StageBreakdown';
import { TreatmentFailureCard } from './components/TreatmentFailureCard';
import { StatBlock } from './components/StatBlock';
import { FoundersStory } from './components/FoundersStory';
import { TrialResults } from './components/TrialResults';
import { IntroducingNerva } from './components/IntroducingNerva';
import { TimelineExpectations } from './components/TimelineExpectations';
import { ComparisonTable } from './components/ComparisonTable';
import { Testimonials } from './components/Testimonials';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
export function App() {
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-0">
      <Header data-id="element-1" />

      <main className="flex-grow" data-id="element-2">
        <Hero data-id="element-3" />

        <ArticleSection eyebrow="If this is you" data-id="element-4">
          <p data-id="element-5">You've memorised the bathrooms.</p>
          <p data-id="element-6">
            Not consciously. It just happened. Every restaurant, every office
            building, every shopping centre — somewhere in the back of your
            mind, you know exactly where the nearest one is. Just in case.
          </p>
          <p data-id="element-7">
            Maybe it's the bloat that arrives by mid-morning regardless of what
            you had for breakfast. The kind that makes you look, as one patient
            put it, "six months pregnant by 4pm." Maybe it's the cramping that
            hits without warning. The meals you now dread rather than enjoy. The
            foods you've quietly eliminated — one by one, then another, then
            another — until you're eating the same five things and your stomach
            is still misbehaving.
          </p>
          <p data-id="element-8">
            You've been to the doctor. Possibly many of them. The tests come
            back normal. The endoscopy showed nothing. The ultrasound was clear.
            "Probably IBS," they say. "Try reducing stress. Here's a pamphlet on
            low-FODMAP."
          </p>

          <PullQuote quote="Doctors say you have IBS because you're crazy. No. IBS makes you crazy — because all you think about 24-7 is food, the bathroom, and your stomach. That's all you think about, all day, every day." attribution="Ashley Walkley, Cedars-Sinai patient story" data-id="element-9" />

          <p data-id="element-10">
            So you went looking for answers yourself. You tried the diets. You
            tried the supplements. Some of them helped a little, for a while.
            None of them stuck. And somewhere in the last few months or years, a
            quiet dread has settled in — the fear that this is just how your
            body works now. That there is no fix. That you missed your chance.
          </p>
          <p data-id="element-11">
            You didn't miss anything. The answer has simply been pointed in the
            wrong direction.
          </p>
          <p data-id="element-12">
            Every treatment you've tried has aimed at your gut. The real problem
            lives elsewhere — in a nerve that runs from the base of your
            brainstem all the way to your colon. A nerve that controls nearly
            everything your digestive system does. And a nerve that modern life
            has been systematically, silently suppressing.
          </p>
        </ArticleSection>

        <ArticleSection eyebrow="The missing piece" title="The Nerve Running Your Gut That Nobody Told You About" image={{
        src: 'https://cdn.magicpatterns.com/uploads/sNxsMz8KaXD8WxknVAhsAy/hf_20260522_090603_4f508ba0-93dd-4be1-adf9-7088e576cf95.png',
        alt: 'Anatomical model showing the brainstem and cervical pathway of the vagus nerve',
        caption: 'The vagus nerve — the longest cranial nerve, running from brainstem to colon.'
      }} data-id="element-13">
          <p data-id="element-14">The vagus nerve is the longest cranial nerve in the human body.</p>
          <p data-id="element-15">
            It originates at the base of the brainstem, travels down through the
            neck, threads through the chest, and reaches all the way to the
            colon — touching the heart, the lungs, the oesophagus, the stomach,
            the small intestine, the liver, and most of the major organs on the
            way down. It is the body's primary communication cable between the
            brain and everything below the neck.
          </p>
          <p data-id="element-16">
            When it is working at full capacity, it is the reason your digestive
            system knows what to do. When to contract. When to relax. When to
            secrete digestive enzymes. When to suppress inflammation in the gut
            wall. When to trigger the cleansing waves that sweep bacteria and
            undigested matter through the intestine between meals.
          </p>

          <Callout variant="key-fact" label="Key Fact" data-id="element-17">
            Roughly 80% of vagus nerve fibres carry signals upward — from the
            gut to the brain, not the other way around. Your gut is sending
            information to your brain four times more than your brain is sending
            it back down. The vagus is the wire that carries that entire
            conversation.
          </Callout>

          <p data-id="element-18">
            The vagus nerve is also the direct controller of something
            gastroenterologists call the Migrating Motor Complex — the wave-like
            contractions that sweep through the small intestine roughly every 90
            minutes during fasting periods, clearing out bacteria, moving
            undigested matter forward, and maintaining the clean environment the
            small intestine needs to function.
          </p>
          <p data-id="element-19">
            When vagal signalling is strong, these waves run on schedule.
            Digestion flows. The gut stays balanced.
          </p>
          <p data-id="element-20">
            When vagal signalling drops below its natural baseline — which is
            what happens in the majority of people with chronic gut dysfunction
            — these waves slow down dramatically. In some cases they stop almost
            entirely.
          </p>
          <p data-id="element-21">
            What happens next is exactly what you've been experiencing for
            months or years. And it begins a cascade that no amount of dietary
            restriction, probiotics, or antibiotics can fix — because none of
            them address the upstream cause.
          </p>
        </ArticleSection>

        <ArticleSection eyebrow="What's actually happening inside you" title="The Four-Stage Breakdown That Begins When Your Vagal Signal Goes Quiet" image={{
        src: 'https://cdn.magicpatterns.com/uploads/evXPqxfsddsUd2rsLkGjCV/hf_20260522_084758_0057b22c-582d-49a0-b7b7-d31ad06b0fec.png',
        alt: 'Cross-section anatomical model of the small intestine wall and villi',
        caption: 'Inside the small intestine: where the cascade begins.'
      }} data-id="element-22">
          <p data-id="element-23">
            This is the sequence your gastroenterologist has probably never
            described to you. Not because it isn't established science — it is —
            but because the standard medical model treats each stage as a
            separate problem rather than seeing them as a single connected
            cascade.
          </p>

          <StageBreakdown data-id="element-24" />

          <Callout variant="science" label="Peer-Reviewed Science" data-id="element-25">
            <p className="mb-4" data-id="element-26">
              In 2000, Dr. Kevin Tracey at the Feinstein Institutes published a
              landmark paper in Nature identifying what he called the
              cholinergic anti-inflammatory pathway — the discovery that the
              vagus nerve directly controls the body's suppression of
              inflammatory cytokines like TNF-α throughout the body. Stimulate
              the vagus, and inflammation is switched off. Let vagal activity
              fall, and inflammation rises unchecked — not from a new disease,
              but from the loss of the body's own built-in regulatory system.
            </p>
            <p className="mb-4" data-id="element-27">
              This single paper opened the entire field of bioelectronic
              medicine, and it explains at a mechanistic level why gut
              dysfunction, chronic inflammation, anxiety, and disrupted sleep
              occur together in the same person with such predictable
              regularity.
            </p>
            <p className="text-sm font-mono text-ink/60" data-id="element-28">
              Tracey KJ. The inflammatory reflex. Nature.
              2002;420(6917):853–859.
            </p>
          </Callout>

          <p data-id="element-29">
            The cramping, the bloat, the bathroom urgency, the food fear, the
            fatigue, the fog — these are not separate problems you are managing.
            They are one problem with several faces. One upstream regulator
            operating below its baseline. One signal, gone quiet.
          </p>
        </ArticleSection>

        <ArticleSection eyebrow="Why this is happening to you" title="Why Modern Life Is Dismantling Your Vagus Nerve — And Why Your Doctor Isn't Connecting the Dots" image={{
        src: 'https://cdn.magicpatterns.com/uploads/xep2GVTa7dnfR6JChqU9dC/hf_20260522_090459_32a11518-75a3-482a-8525-28e634fafc0c.png',
        alt: 'Blister packs of prescription pills and capsules on a slate surface',
        caption: 'The prescriptions that quietly compound the problem they were meant to solve.'
      }} data-id="element-30">
          <p data-id="element-31">
            Vagal suppression is not a rare condition. It is the predictable
            consequence of living the way most people in the developed world now
            live.
          </p>
          <p data-id="element-32">
            The vagus nerve is maintained — or degraded — by the inputs your
            body receives daily. Slow, deep breathing raises vagal tone. Cold
            exposure activates it. Extended fasting periods allow the Migrating
            Motor Complex to run its full cleansing cycles. A rich, diverse gut
            microbiome feeds upward afferent signals that keep vagal activity
            strong.
          </p>
          <p data-id="element-33">
            Most people get almost none of these inputs consistently. In their
            place: chronic low-grade stress that locks the nervous system in
            sympathetic overdrive, ultra-processed food that devastates
            microbial diversity, and the two medical interventions that —
            unintentionally — do some of the worst damage.
          </p>

          <h3 className="text-2xl font-serif font-medium text-forest mt-12 mb-6" data-id="element-34">
            The Two Treatments That Make It Worse
          </h3>

          <TreatmentFailureCard title="Antibiotics — Rifaximin and others" data-id="element-35">
            <p className="mb-4" data-id="element-36">
              Rifaximin is the standard pharmaceutical treatment for SIBO. It
              works — for a while. In clinical data, 51.6% of patients don't
              respond to the initial course. Among those who do respond, 84.4%
              relapse within 18 weeks.
            </p>
            <p className="mb-4" data-id="element-37">
              Here's why: Rifaximin attacks the bacterial overgrowth in the
              small intestine, but does nothing to restore the vagal signal that
              allowed the bacteria to take hold in the first place. The moment
              the antibiotic course ends, vagal suppression is still present,
              the Migrating Motor Complex is still running slow, and the
              conditions that created the overgrowth are exactly as they were.
              The bacteria return. Often within weeks.
            </p>
            <p data-id="element-38">
              Meanwhile, each antibiotic course further reduces microbial
              diversity in the large intestine — the very diversity that feeds
              the upward vagal signals the gut depends on. Research from Uppsala
              University found that a single course of antibiotics can alter gut
              microbiome composition for four to eight years. The treatment is
              degrading the biological infrastructure it needs to work.
            </p>
          </TreatmentFailureCard>

          <TreatmentFailureCard title="PPIs — Omeprazole, Esomeprazole, and others" data-id="element-39">
            <p className="mb-4" data-id="element-40">
              PPI use in the US has more than doubled since 1999. They are
              prescribed for heartburn, reflux, and gastritis — often as
              long-term solutions.
            </p>
            <p data-id="element-41">
              The problem: chronically suppressing stomach acid alters the
              intestinal environment in ways that actively promote bacterial
              overgrowth. Stomach acid is the body's first line of defence
              against bacteria entering the small intestine. Eliminate it, and
              the door is open. This is one of the most well-documented pathways
              from heartburn treatment to chronic IBS — a condition the PPI was
              never designed to address. The prescription that started as a
              solution to reflux becomes the upstream cause of a gut disorder
              that looks completely unrelated.
            </p>
          </TreatmentFailureCard>

          <StatBlock stat="4–8 yrs" caption="According to researchers at Uppsala University, even a single course of certain antibiotics leaves measurable traces in the gut microbiome for up to four to eight years — directly disrupting the microbial signals that feed vagal activity from the gut to the brain." data-id="element-42" />

          <p data-id="element-43">
            There is one more factor worth naming because it has affected more
            people than most doctors have acknowledged: COVID-19. Postmortem
            analysis has confirmed SARS-CoV-2 RNA inside vagus nerve tissue
            itself, with inflammatory cell infiltration. A 2022 study of 320
            long-COVID patients found that 76.7% had measurable autonomic
            dysfunction — and gastrointestinal symptoms were the single most
            affected domain, present in 91.6% of those patients.
          </p>

          <Callout variant="warning" label="If Your Gut Problems Started After COVID" data-id="element-44">
            If your bloating, urgency, or IBS symptoms appeared suddenly after a
            COVID infection — or worsened dramatically in the months following
            one — and your doctors have been unable to explain why, this is
            almost certainly part of the answer. The virus does not just affect
            the lungs. Postmortem studies have found it inside the vagus nerve
            itself. What you are experiencing is not psychosomatic. It is
            neurological. And it is addressable.
          </Callout>
        </ArticleSection>

        <ArticleSection eyebrow="The reason you're still here" title="Why Every Treatment You've Tried Has Failed — And Why That Failure Was Predictable" image={{
        src: 'https://cdn.magicpatterns.com/uploads/9YvYb49XKKv4AHVhuwWNzv/hf_20260522_084815_e85c4e91-e850-4381-b25c-2fd8b69a84c3.png',
        alt: 'Flat lay of restrictive elimination diet ingredients — greens, rice, cucumber, chicken, lemon, and supplement bottles',
        caption: "Every approach you've tried has targeted the gut — never the nerve that regulates it."
      }} data-id="element-45">
          <p data-id="element-46">
            This is not a criticism of anything you've done. Every decision you
            made was rational given what you were told. The failure was not
            yours.
          </p>
          <p data-id="element-47">
            The failure was architectural: every solution you were given
            targeted the gut. None of them targeted the nerve that regulates it.
          </p>

          <TreatmentFailureCard title="Low-FODMAP Diet" data-id="element-48">
            <p className="mb-4" data-id="element-49">
              Low-FODMAP works by removing the carbohydrates that gut bacteria
              ferment — reducing the gas and bloating that bacterial overgrowth
              produces. For many people it provides real relief. About 50–70% of
              IBS patients improve on it.
            </p>
            <p data-id="element-50">
              But it is not treating bacterial overgrowth. It is starving the
              bacteria of their fuel while leaving them in place. The moment
              FODMAPs are reintroduced — even carefully — the bacteria are still
              there, the vagal signal is still suppressed, and symptoms return.
              Most people end up on a permanent elimination diet of increasing
              restriction, never reaching the actual cause.
            </p>
          </TreatmentFailureCard>

          <TreatmentFailureCard title="Probiotics" data-id="element-51">
            <p data-id="element-52">
              A clinical trial published in Cell found that probiotic use led to
              delays in the normal re-establishment of healthy gut bacteria —
              the opposite of the intended effect. For patients with SIBO
              specifically, adding more bacteria into an already overrun
              environment where the clearance mechanism is broken can worsen
              gas, bloating, and discomfort. The small intestine does not need
              more bacteria. It needs the mechanism that keeps it clean to be
              restored.
            </p>
          </TreatmentFailureCard>

          <TreatmentFailureCard title="SSRIs and Anti-Anxiety Medication" data-id="element-53">
            <p data-id="element-54">
              It is now standard clinical practice to prescribe SSRIs for IBS —
              on the basis that gut and mood share a common pathway, which is
              partially true. But SSRIs modify serotonin signalling in the brain
              without restoring the upstream vagal regulation driving the gut
              dysfunction. The result, for most patients, is a modest blunting
              of anxiety and discomfort while the condition itself continues
              undisturbed. The symptoms are quieter. The cause is untouched.
            </p>
          </TreatmentFailureCard>

          <PullQuote quote="Eight months of treating SIBO and candida overgrowth. Eight months of prescriptions, vitamins, supplements, low-FODMAP diet, try this and try that. Eight months of not feeling myself." attribution="Liza Lovett, Medium" data-id="element-55" />

          <p data-id="element-56">
            What all of these approaches share is the same fundamental logic:
            treat the gut directly, at the level of its contents or its
            chemistry. None of them ask the question that changes everything:
          </p>
          <p className="font-medium text-forest text-xl font-serif my-6" data-id="element-57">
            Why is the gut behaving this way? What upstream regulator has failed
            — and can it be restored?
          </p>
          <p data-id="element-58">
            That question has an answer. And it has had one since 2000, when a
            surgeon-scientist named Kevin Tracey published a paper in Nature
            that should have changed the standard of care for gut dysfunction
            entirely. It still hasn't reached most gastroenterologists' offices.
            But it is the foundation of what I'm going to show you next.
          </p>
        </ArticleSection>

        <FoundersStory data-id="element-59" />

        <ArticleSection eyebrow="The breakthrough" title="The 2024 Trial Your Gastroenterologist Hasn't Read Yet" image={{
        src: 'https://cdn.magicpatterns.com/uploads/1eEHAyRpdrmpQaVhjwZeVn/hf_20260522_085839_2e208405-0577-41a8-94e9-d37a38f8bb13.png',
        alt: 'Researcher pipetting a sample into a clear 96-well plate in a clinical laboratory',
        caption: 'American Journal of Gastroenterology, December 2024 — peer-reviewed, statistically significant.'
      }} data-id="element-60">
          <p data-id="element-61">
            In December 2024, researchers at the First Affiliated Hospital of
            the University of Science and Technology of China published a
            clinical trial in the American Journal of Gastroenterology — one of
            the most respected peer-reviewed journals in gut medicine.
          </p>
          <p data-id="element-62">
            They took patients with IBS-C — constipation-predominant irritable
            bowel syndrome — and applied non-invasive vagus nerve stimulation
            through electrodes placed on the skin for four weeks. No drugs. No
            dietary changes. No surgery. Just daily vagal stimulation through
            the skin.
          </p>

          <TrialResults data-id="element-63" />

          <p data-id="element-64">
            This is not the only evidence. Vagus nerve stimulation has been
            FDA-approved since 1997 — first for refractory epilepsy, then for
            treatment-resistant depression. The FDA cleared gammaCore, a
            non-invasive cervical VNS device, for cluster headache and migraine
            in 2017 — establishing the neck as a validated, regulated pathway to
            the vagus nerve. The broader clinical literature on non-invasive VNS
            now includes 50+ peer-reviewed studies. And the French researcher
            Bruno Bonaz ran an open-label pilot showing that vagal stimulation
            produced measurable improvements in patients with Crohn's disease —
            another inflammatory gut condition driven by the same cholinergic
            anti-inflammatory pathway that Tracey identified in 2000.
          </p>

          <Callout variant="key-fact" label="Why This Is Different From Everything Else" data-id="element-65">
            Every treatment you've been given has acted on the gut directly —
            changing its contents, its bacteria, its chemistry, its motility
            drugs. Vagal stimulation acts on the regulator. It is the difference
            between repeatedly repainting a wall that keeps getting damp, and
            finally fixing the pipe.
          </Callout>

          <p data-id="element-66">
            The question then becomes: how do you actually stimulate the vagus
            nerve at home, safely, without surgery or a prescription?
          </p>
          <p data-id="element-67">The answer is in the anatomy.</p>
        </ArticleSection>

        <ArticleSection eyebrow="The mechanism" title="Why the Neck — And Why This Is the Most Direct Route to the Nerve" data-id="element-68">
          <p data-id="element-69">
            The vagus nerve is almost entirely internal — running deep through
            the chest and abdomen, protected by tissue. But in the neck, at a
            region called the carotid triangle, the vagus nerve travels close
            enough to the surface of the skin that a precisely-calibrated
            electrical signal can reach it transcutaneously — without breaking
            the skin, without surgery, without a prescription.
          </p>

          <div className="my-10 rounded-2xl overflow-hidden shadow-sm" data-id="element-70">
            <img src="https://cdn.magicpatterns.com/uploads/cJ7wCvuFRGGN6StceT7o6S/hf_20260522_091022_e0c06c0c-26f2-408b-ac31-12f401c7dc85.png" alt="Anatomical visualization of the vagus nerve glowing along its path from the brain through the neck to the digestive organs" className="w-full h-[420px] sm:h-[520px] object-contain bg-ink" data-id="element-71" />
          </div>

          <p data-id="element-72">
            This is the same anatomical target used by gammaCore, the
            FDA-cleared cervical VNS device for cluster headache and migraine.
            It is the same approach validated in peer-reviewed clinical
            literature on non-invasive VNS for inflammatory conditions,
            autonomic dysfunction, and gut motility disorders. The neck is not a
            workaround. It is the most direct non-surgical access point to the
            vagus nerve in the human body.
          </p>

          <Callout variant="note" label="Why Other Devices Don't Reach the Nerve" data-id="element-73">
            Wrist-worn vibration devices (Apollo Neuro) and chest vibration
            devices (Sensate) produce a calming sensation — but they are not
            electrically stimulating the vagus nerve. Vibration at the wrist or
            chest does not reach the nerve. Real transcutaneous VNS requires a
            precisely-tuned electrical signal delivered to the skin over the
            vagus nerve's cervical pathway. That is the difference between a
            relaxation tool and a nerve stimulator.
          </Callout>

          <Callout variant="note" label="Is It Safe?" data-id="element-74">
            Across 50+ peer-reviewed non-invasive VNS studies, no serious
            adverse events have been reported. The most common experience is
            mild, transient tingling at the electrode site during the session.
            The overall discontinuation rate in pooled neuromodulation
            literature is 2.6% — overwhelmingly due to personal preference, not
            adverse effects. Unlike medications, vagal stimulation does not
            alter gut chemistry, deplete the microbiome, or create dependency.
            It restores a signal. It does not introduce a new one.
          </Callout>

          <p data-id="element-75">
            When a precise electrical signal is delivered to the skin over the
            cervical vagus for 10 minutes per day, it travels along the vagal
            pathway to the nucleus tractus solitarii in the brainstem — the same
            pathway activated by deep slow breathing, cold water immersion,
            extended fasting, and vocal vibration. The body cannot distinguish
            between a naturally-generated vagal signal and a precisely-tuned
            external one. The brainstem receives the signal and responds the
            same way it always has: activating the parasympathetic cascade,
            restoring gut motility, and engaging the cholinergic
            anti-inflammatory pathway.
          </p>
          <p data-id="element-76">This is the technology Nerva™ is built on.</p>
        </ArticleSection>

        <IntroducingNerva data-id="element-77" />

        <ArticleSection fullWidth data-id="element-78">
          <TimelineExpectations data-id="element-79" />
        </ArticleSection>

        <ArticleSection eyebrow="How it compares" title="Nerva™ vs. Everything You've Already Tried" fullWidth data-id="element-80">
          <ComparisonTable data-id="element-81" />
        </ArticleSection>

        <ArticleSection eyebrow="Our promise to you" title="The Guarantee That Makes This Decision Risk-Free" image={{
        src: 'https://cdn.magicpatterns.com/uploads/fUYZuHYRWgQkoPEMtyMJSp/hf_20260516_165139_3053f038-2c2a-4791-a8ee-c87a8245967b.png',
        alt: 'A woman reading on her couch in soft lamplight, wearing the Nerva cervical VNS device around her neck',
        caption: '60 days. Full refund. No fight.'
      }} data-id="element-82">
          <p data-id="element-83">
            We are going to say something that most companies in this space
            won't say.
          </p>
          <p data-id="element-84">
            We know your track record with treatments. We know you have spent
            money — probably a lot of it — on things that were supposed to work
            and didn't. We know Rifaximin didn't hold. We know the supplements
            were a wash. We know the functional medicine practice took four
            figures and gave you a SIBO diagnosis you already suspected and a
            prescription you'd already been on.
          </p>
          <p data-id="element-85">We are not going to ask you to take that risk again.</p>

          <Callout variant="guarantee" label="The Nerva™ 60-Day Guarantee" title="Try It for 60 Days. If Your Gut Hasn't Improved, You Don't Pay." data-id="element-86">
            <p className="mb-4" data-id="element-87">
              Use Nerva™ daily for 60 days. If you don't experience a
              meaningful improvement in your gut symptoms — less bloating, less
              urgency, less food fear, more regularity — send it back. Full
              refund. No questions. No restocking fee. No fight. (We ask only
              that you use it for a minimum of 14 days before requesting a
              return — because the clinical data shows cumulative benefit, and
              we want you to give it a genuine chance.)
            </p>
            <p data-id="element-88">
              The worst case: you are out the cost of return shipping. Not
              $3,800. Not a year of antibiotic prescriptions. Not another six
              months of functional medicine appointments. Return shipping.
            </p>
          </Callout>

          <p data-id="element-89">
            We can offer this because we believe in what the clinical literature
            says — and because we know that the people who need this the most
            are the ones who have been burned the most, and the only way to earn
            their trust is to remove the risk entirely.
          </p>
        </ArticleSection>

        <ArticleSection eyebrow="What users are reporting" title="Real People. Measurable Outcomes. No Stock Photos." data-id="element-90">
          <p className="mb-8" data-id="element-91">
            Our avatar has been burned by marketing before. So we'll say this
            plainly: these are real accounts from real users. First names and
            locations only, at their request.
          </p>

          <Testimonials data-id="element-92" />

          <div className="mt-12 bg-beige/30 p-6 rounded-xl border border-taupe/20" data-id="element-93">
            <h4 className="text-sm font-bold uppercase tracking-widest text-sage mb-3" data-id="element-94">
              A Note on Regulatory Status
            </h4>
            <p className="text-sm text-ink/70 leading-relaxed m-0" data-id="element-95">
              Nerva™ carries FDA Non-Significant Risk Designation and is built
              to medical-grade electroceutical standards — the same regulatory
              category as other non-invasive transcutaneous VNS devices. It is
              not FDA-cleared as a treatment for IBS or any specific condition —
              no consumer transcutaneous VNS device currently is. What it is: a
              rigorously engineered non-invasive cervical vagal stimulator built
              on the same validated pathway and waveform parameters used in
              peer-reviewed clinical literature on vagal modulation for gut and
              inflammatory conditions. If you want to show this to your doctor,
              a one-page Clinician Information Sheet with full mechanism
              explanation, trial citations, and contraindications is available
              on the product page.
            </p>
          </div>
        </ArticleSection>

        <ArticleSection eyebrow="The next step" title="You've Been Looking for an Answer Long Enough" image={{
        src: 'https://cdn.magicpatterns.com/uploads/oy2xarh6YGHRXKXt9ecoty/hf_20260522_090857_0ec69ab8-20d9-47f6-8576-977985f8836a.png',
        alt: 'A woman walking down a sunlit dirt path through tall golden grass in warm late-afternoon light',
        caption: 'The first thing aimed at the actual cause.'
      }} data-id="element-96">
          <p data-id="element-97">
            If what you've read here has made sense of symptoms you've been
            living with for months or years — the bloating, the urgency, the
            food fear, the fatigue that nobody could explain — then you have
            just found the explanation that should have been offered to you
            years ago.
          </p>
          <p data-id="element-98">
            The vagus nerve is the upstream regulator. Vagal Signal Collapse™
            is the mechanism behind your symptoms. Targeted Cervical Vagal
            Restoration — through a 10-minute daily session with Nerva™ — is
            the intervention that the clinical literature supports.
          </p>
          <p data-id="element-99">
            This is not another supplement. It is not another elimination diet.
            It is not another antibiotic course that will work for six weeks and
            then stop.
          </p>
          <p className="text-xl font-serif font-medium text-forest my-8" data-id="element-100">
            It is the first thing aimed at the actual cause.
          </p>

          <PullQuote quote="I'm getting a little emotional while writing this because if someone had told me I would be able to have a life after those hard years, I wouldn't have believed them." attribution="Camila, aboutibs.org" data-id="element-101" />
        </ArticleSection>

        <FinalCTA data-id="element-102" />
      </main>

      <Footer data-id="element-103" />
    </div>;
}